import {
  Button,
  Checkbox,
  DatePicker,
  Icon,
  Input,
  Modal,
  Tooltip,
} from "@legal-500/legal500-design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  KeyspaceDirectoryEntry,
  ReleaseDefinition,
  ReleasePublicationOverride,
} from "@legal-500/redis-db-manager-types";
import {
  dbManagerService,
  OTHER_SLOT_SYNC_RUNNING,
  type OtherSlotSyncRunningDetails,
  type TriggerSyncBody,
} from "../../../api/dbManager.service.js";
import type { ApiError } from "../../../api/client.js";
import { usePublications } from "../../../hooks/usePublications.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { SearchableMultiSelect } from "../../../components/Form/SearchableSelect/index.js";
import type { LegacyPublication } from "../../../api/legacy-publications.service.js";
import { formatTimestamp } from "./slotStatus.js";
import styles from "./DbManager.module.css";

type PostStatus = "publish" | "draft" | "pending" | "trash" | "new";

const POST_STATUSES: PostStatus[] = ["publish", "draft", "pending", "trash", "new"];

const DEFAULT_POST_STATUSES: PostStatus[] = ["publish"];

type DemotionPolicy = ReleaseDefinition["demotionPolicy"];

const KEYSPACE_PATTERN = /^[a-z0-9-]+$/;

const PUBLICATIONS_STATUS_FILTER = "Active,Researching";

type OtherSlotConflict = {
  body: TriggerSyncBody;
  details: OtherSlotSyncRunningDetails | null;
  message: string;
};

type TriggerSyncModalProps = {
  isOpen: boolean;
  initialKeyspace?: string;
  family: string;
  initialPageTypes?: string[] | null;
  initialClearAllData?: boolean;
  allKeyspaces?: KeyspaceDirectoryEntry[];
  onClose: () => void;
  onSubmitted: (runId: string) => void;
};

export function TriggerSyncModal({
  isOpen,
  initialKeyspace = "",
  family,
  initialPageTypes,
  initialClearAllData,
  allKeyspaces,
  onClose,
  onSubmitted,
}: TriggerSyncModalProps) {
  const [keyspace, setKeyspace] = useState(initialKeyspace);
  const [keyspaceTouched, setKeyspaceTouched] = useState(false);
  const [clearAllData, setClearAllData] = useState(initialClearAllData ?? true);
  const [allPageTypes, setAllPageTypes] = useState<string[] | null>(null);
  const [selectedPageTypes, setSelectedPageTypes] = useState<Set<string>>(new Set());
  const [pageTypesExpanded, setPageTypesExpanded] = useState(false);
  const [pageTypesLoading, setPageTypesLoading] = useState(false);
  const [pageTypesError, setPageTypesError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [seedFromKeyspace, setSeedFromKeyspace] = useState<string | null>(null);
  const [releaseExpanded, setReleaseExpanded] = useState(false);
  const [pubOverrides, setPubOverrides] = useState<Map<number, PostStatus[]>>(new Map());
  const [demotionPolicy, setDemotionPolicy] = useState<DemotionPolicy>("none");
  const [asAt, setAsAt] = useState<string | undefined>(undefined);
  const [otherSlotConflict, setOtherSlotConflict] = useState<OtherSlotConflict | null>(null);

  const { asAtMin, asAtMax } = useMemo(() => buildAsAtDateBounds(), []);

  const { data: publicationsData } = usePublications(
    { status: PUBLICATIONS_STATUS_FILTER },
    { enabled: isOpen },
  );
  const allPublications = useMemo(() => publicationsData?.publications ?? [], [publicationsData]);

  const completedSeedKeyspaces = useMemo(
    () =>
      (allKeyspaces ?? []).filter(
        entry => entry.status === "completed" && entry.keyspace !== keyspace,
      ),
    [allKeyspaces, keyspace],
  );

  const isSeedingLocked = seedFromKeyspace !== null;

  useEffect(() => {
    if (!isOpen) {
      setOtherSlotConflict(null);
      return;
    }
    setKeyspace(initialKeyspace);
    setKeyspaceTouched(false);
    setClearAllData(initialClearAllData ?? true);
    setSubmitError(null);
    setPageTypesExpanded(false);
    setSeedFromKeyspace(null);
    setReleaseExpanded(false);
    setPubOverrides(new Map());
    setDemotionPolicy("none");
    setAsAt(undefined);
    setOtherSlotConflict(null);
  }, [isOpen, initialKeyspace, initialClearAllData]);

  useEffect(() => {
    if (!isOpen) return;
    setPageTypesLoading(true);
    setPageTypesError(null);
    let cancelled = false;
    dbManagerService
      .listPageTypes()
      .then(types => {
        if (cancelled) return;
        setAllPageTypes(types);
        const initialSelection =
          initialPageTypes && initialPageTypes.length > 0
            ? new Set(initialPageTypes)
            : new Set(types);
        setSelectedPageTypes(initialSelection);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setPageTypesError(err.message);
      })
      .finally(() => {
        if (cancelled) return;
        setPageTypesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, initialPageTypes]);

  const keyspaceError = useMemo(
    () => deriveKeyspaceError(keyspace, keyspaceTouched),
    [keyspace, keyspaceTouched],
  );

  const isAllPageTypesSelected =
    allPageTypes !== null && selectedPageTypes.size === allPageTypes.length;
  const isSubsetSelected =
    allPageTypes !== null &&
    selectedPageTypes.size > 0 &&
    selectedPageTypes.size < allPageTypes.length;
  const hasNoneSelected = allPageTypes !== null && selectedPageTypes.size === 0;

  const releaseDefinition = useMemo(
    () => buildReleaseDefinition({ pubOverrides, demotionPolicy, asAt }),
    [pubOverrides, demotionPolicy, asAt],
  );

  const isReleaseAtProductionDefault = releaseDefinition === undefined;

  const demotedPublications = useMemo(
    () => computeDemotedPublications(pubOverrides, demotionPolicy, allPublications),
    [pubOverrides, demotionPolicy, allPublications],
  );

  const commandPreview = useMemo(
    () =>
      buildCommandPreview({
        keyspace,
        clearAllData,
        allPageTypes,
        selectedPageTypes,
        seedFromKeyspace,
        releaseDefinition,
      }),
    [keyspace, clearAllData, allPageTypes, selectedPageTypes, seedFromKeyspace, releaseDefinition],
  );

  const submitDisabled =
    submitting || pageTypesLoading || !!keyspaceError || keyspace.length === 0 || hasNoneSelected;

  const handleTogglePageType = (name: string, checked: boolean) => {
    setSelectedPageTypes(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(name);
      } else {
        next.delete(name);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (allPageTypes) setSelectedPageTypes(new Set(allPageTypes));
  };

  const handleDeselectAll = () => {
    setSelectedPageTypes(new Set());
  };

  const publicationOptions = useMemo(
    () =>
      allPublications.map(pub => ({
        value: pub.id,
        label: `${pub.publication} (${pub.edition})${pub.publicationStatus ? ` — ${pub.publicationStatus}` : ""}`,
      })),
    [allPublications],
  );

  const selectedPublicationIds = useMemo(() => Array.from(pubOverrides.keys()), [pubOverrides]);

  const handleSelectedPublicationsChange = (next: number[]) => {
    setPubOverrides(prev => {
      const updated = new Map<number, PostStatus[]>();
      for (const id of next) {
        updated.set(id, prev.get(id) ?? [...DEFAULT_POST_STATUSES]);
      }
      return updated;
    });
  };

  const handleTogglePostStatus = (publicationId: number, status: PostStatus, checked: boolean) => {
    setPubOverrides(prev => {
      const current = prev.get(publicationId) ?? [];
      let nextStatuses: PostStatus[];
      if (checked) {
        nextStatuses = current.includes(status) ? current : [...current, status];
      } else {
        if (current.length <= 1) return prev;
        nextStatuses = current.filter(entry => entry !== status);
      }
      const updated = new Map(prev);
      updated.set(publicationId, nextStatuses);
      return updated;
    });
  };

  const handleRemovePublication = (publicationId: number) => {
    setPubOverrides(prev => {
      const updated = new Map(prev);
      updated.delete(publicationId);
      return updated;
    });
  };

  const handleSubmit = async () => {
    setKeyspaceTouched(true);
    if (keyspaceError || hasNoneSelected || allPageTypes === null) return;
    if (!keyspace) return;

    const pageTypesPayload = isAllPageTypesSelected ? null : Array.from(selectedPageTypes);

    await sendTriggerSync({
      keyspace,
      family,
      clearAllData,
      pageTypes: pageTypesPayload,
      seedFromKeyspace,
      releaseDefinition,
    });
  };

  const sendTriggerSync = async (body: TriggerSyncBody) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const response = await dbManagerService.triggerSync(body);
      setOtherSlotConflict(null);
      onSubmitted(response.runId);
    } catch (err) {
      const conflict = body.force ? null : toOtherSlotConflict(err, body);
      if (conflict) {
        setOtherSlotConflict(conflict);
      } else {
        setOtherSlotConflict(null);
        setSubmitError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (otherSlotConflict) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Another slot is already syncing"
        size="small"
        blurBackground
      >
        <OtherSlotConflictConfirm
          conflict={otherSlotConflict}
          submitting={submitting}
          onCancel={() => setOtherSlotConflict(null)}
          onConfirm={() => sendTriggerSync({ ...otherSlotConflict.body, force: true })}
        />
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Trigger sync" size="medium" blurBackground>
      <div className={styles.modalBody}>
        {submitError && <div className={styles.errorBanner}>{submitError}</div>}

        <div className={styles.formField}>
          <label className={styles.fieldLabel} htmlFor="trigger-sync-keyspace">
            Slot name
          </label>
          <Input
            id="trigger-sync-keyspace"
            value={keyspace}
            onChange={event => setKeyspace(event.target.value)}
            onBlur={() => setKeyspaceTouched(true)}
            placeholder="core-2026-04"
            error={keyspaceError ?? undefined}
            helperText={keyspaceError ? undefined : "Must match [a-z0-9-]+"}
          />
        </div>

        {completedSeedKeyspaces.length > 0 && (
          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="trigger-sync-seed-from">
              Seed from existing slot
            </label>
            <select
              id="trigger-sync-seed-from"
              value={seedFromKeyspace ?? ""}
              onChange={event => {
                const selected = event.target.value;
                setSeedFromKeyspace(selected === "" ? null : selected);
                if (selected !== "") {
                  setClearAllData(true);
                }
              }}
            >
              <option value="">Don&apos;t seed (default)</option>
              {completedSeedKeyspaces.map(entry => (
                <option key={entry.keyspace} value={entry.keyspace}>
                  {entry.keyspace}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className={styles.formField}>
          <Checkbox
            id="trigger-sync-clear"
            label="Clear all existing data in slot first (recommended)"
            checked={isSeedingLocked ? true : clearAllData}
            onChange={event => {
              if (!isSeedingLocked) setClearAllData(event.target.checked);
            }}
            disabled={isSeedingLocked}
          />
          {isSeedingLocked && (
            <span className={styles.fieldHint}>Required when seeding from another slot</span>
          )}
        </div>

        {isSeedingLocked && (
          <div className={styles.infoHint} data-testid="seed-from-page-hint">
            Pages not selected will be carried over from the seed slot. Only the selected pages will
            be freshly synced.
          </div>
        )}

        <details
          className={styles.expander}
          open={pageTypesExpanded}
          onToggle={event => setPageTypesExpanded((event.target as HTMLDetailsElement).open)}
        >
          <summary>Customize page types (optional)</summary>
          <div className={styles.expanderInner}>
            {pageTypesLoading && <LoadingState message="Loading page types..." />}
            {pageTypesError && <div className={styles.errorBanner}>{pageTypesError}</div>}
            {!pageTypesLoading && allPageTypes && (
              <>
                <div className={styles.checkboxGridActions}>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    onClick={handleSelectAll}
                    type="button"
                  >
                    Select all
                  </Button>
                  <Button
                    variant="text"
                    color="default"
                    size="small"
                    onClick={handleDeselectAll}
                    type="button"
                  >
                    Deselect all
                  </Button>
                </div>
                <div className={styles.checkboxGrid}>
                  {allPageTypes.map(name => (
                    <Checkbox
                      key={name}
                      id={`pt-${name}`}
                      label={name}
                      checked={selectedPageTypes.has(name)}
                      onChange={event => handleTogglePageType(name, event.target.checked)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </details>

        <details
          className={styles.expander}
          open={releaseExpanded}
          onToggle={event => setReleaseExpanded((event.target as HTMLDetailsElement).open)}
          data-testid="release-section"
        >
          <summary>Release (optional)</summary>
          <div className={styles.expanderInner}>
            <SearchableMultiSelect
              name="trigger-sync-publications"
              label="Publication overrides"
              value={selectedPublicationIds}
              options={publicationOptions}
              onChange={handleSelectedPublicationsChange}
              placeholder="Search publications to override..."
              helperText="Selected publications include the chosen post statuses. Others flow through with publish only."
            />

            {selectedPublicationIds.length > 0 && (
              <div className={styles.releasePubRows} data-testid="release-pub-rows">
                {selectedPublicationIds.map(publicationId => {
                  const pub = allPublications.find(entry => entry.id === publicationId);
                  const statuses = pubOverrides.get(publicationId) ?? [];
                  const onlyOneSelected = statuses.length <= 1;
                  return (
                    <div
                      key={publicationId}
                      className={styles.releasePubRow}
                      data-testid={`release-pub-row-${publicationId}`}
                    >
                      <div className={styles.releasePubRowHeader}>
                        <span className={styles.releasePubRowTitle}>
                          {pub
                            ? `${pub.publication} (${pub.edition})`
                            : `Publication ${publicationId}`}
                        </span>
                        <Button
                          variant="text"
                          color="default"
                          size="small"
                          type="button"
                          onClick={() => handleRemovePublication(publicationId)}
                        >
                          Remove
                        </Button>
                      </div>
                      <div className={styles.releasePubRowStatuses}>
                        {POST_STATUSES.map(status => {
                          const isChecked = statuses.includes(status);
                          const disabled = isChecked && onlyOneSelected;
                          return (
                            <Checkbox
                              key={status}
                              id={`release-pub-${publicationId}-status-${status}`}
                              label={status}
                              checked={isChecked}
                              disabled={disabled}
                              onChange={event =>
                                handleTogglePostStatus(publicationId, status, event.target.checked)
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className={styles.formField}>
              <span className={styles.fieldLabel}>
                Demotion policy
                <Tooltip
                  title="Demotion policy"
                  content={<DemotionPolicyHelp />}
                  position="right"
                  className={styles.demotionPolicyHelp}
                >
                  <span
                    className={styles.demotionPolicyHelpIcon}
                    tabIndex={0}
                    role="img"
                    aria-label="Demotion policy help"
                    data-testid="release-policy-help"
                  >
                    <Icon icon="info" size="small" aria-hidden="true" />
                  </span>
                </Tooltip>
              </span>
              <div className={styles.releasePolicyRow} role="radiogroup">
                {(["none", "one"] as const).map(policy => (
                  <label key={policy} className={styles.confirmInline}>
                    <input
                      type="radio"
                      name="trigger-sync-demotion-policy"
                      value={policy}
                      checked={demotionPolicy === policy}
                      onChange={() => setDemotionPolicy(policy)}
                      data-testid={`release-policy-${policy}`}
                    />
                    {policy}
                  </label>
                ))}
              </div>
              <DemotionPreview
                publications={demotedPublications}
                hasOverrides={selectedPublicationIds.length > 0}
                policy={demotionPolicy}
              />
            </div>

            <div className={styles.formField}>
              <span className={styles.fieldLabel}>
                As-at date (optional)
                <Tooltip
                  title="As-at date"
                  content="Resolve fulfilment entitlements as if today were this date. Use to preview a future edition before subscriptions roll over."
                  position="right"
                  className={styles.demotionPolicyHelp}
                >
                  <span
                    className={styles.demotionPolicyHelpIcon}
                    tabIndex={0}
                    role="img"
                    aria-label="As-at date help"
                    data-testid="release-as-at-help"
                  >
                    <Icon icon="info" size="small" aria-hidden="true" />
                  </span>
                </Tooltip>
              </span>
              <DatePicker
                id="trigger-sync-as-at"
                value={asAt ?? ""}
                min={asAtMin}
                max={asAtMax}
                onChange={event => {
                  const next = event.target.value;
                  setAsAt(next === "" ? undefined : next);
                }}
              />
            </div>

            {isReleaseAtProductionDefault && (
              <div className={styles.fieldHint}>
                No overrides set. Sync will use the production release definition.
              </div>
            )}
          </div>
        </details>

        {hasNoneSelected && (
          <div className={styles.fieldError}>At least one page type must be selected.</div>
        )}

        {!hasNoneSelected && clearAllData && isSubsetSelected && (
          <div className={styles.warningBanner}>
            Slot <code>{keyspace}</code> will be cleared, then only the selected pages built. The
            slot will be incomplete and should not be made active until a full sync runs.
          </div>
        )}

        <div className={styles.formField}>
          <span className={styles.fieldLabel}>Resulting sync command</span>
          <div className={styles.commandPreview} data-testid="command-preview">
            {commandPreview}
          </div>
        </div>

        <div className={styles.actions}>
          <Button variant="text" color="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={submitDisabled}
          >
            {submitting ? "Triggering..." : "Trigger sync"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

type OtherSlotConflictConfirmProps = {
  conflict: OtherSlotConflict;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

function OtherSlotConflictConfirm({
  conflict,
  submitting,
  onCancel,
  onConfirm,
}: OtherSlotConflictConfirmProps) {
  const { details } = conflict;
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  return (
    <div className={styles.modalBody}>
      {details ? (
        <>
          <div className={styles.confirmationLine}>
            <span className={styles.confirmationKey}>Syncing now</span>
            <span className={styles.confirmationValue}>
              <code>{details.runningKeyspace}</code>
            </span>
          </div>
          <div className={styles.confirmationLine}>
            <span className={styles.confirmationKey}>Started</span>
            <span className={styles.confirmationValue}>{formatTimestamp(details.startedAt)}</span>
          </div>
          <div className={styles.confirmationLine}>
            <span className={styles.confirmationKey}>Started by</span>
            <span className={styles.confirmationValue}>{details.triggeredBy}</span>
          </div>
        </>
      ) : (
        <div className={styles.warningBanner}>{conflict.message}</div>
      )}

      <p>
        Starting <code>{conflict.body.keyspace}</code> as well means two syncs run at once, which
        doubles the load on shared services. You can wait for the other sync to finish, or start
        this one anyway.
      </p>

      <div className={styles.actions}>
        <Button
          ref={cancelRef}
          variant="text"
          color="default"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={submitting}>
          {submitting ? "Starting..." : "Start anyway"}
        </Button>
      </div>
    </div>
  );
}

function toOtherSlotConflict(err: unknown, body: TriggerSyncBody): OtherSlotConflict | null {
  if (!(err instanceof Error)) return null;
  const apiError: ApiError = err;
  if (apiError.status !== 409 || apiError.code !== OTHER_SLOT_SYNC_RUNNING) return null;
  return {
    body,
    details: isOtherSlotSyncRunningDetails(apiError.detailsRaw) ? apiError.detailsRaw : null,
    message: apiError.message,
  };
}

function isOtherSlotSyncRunningDetails(value: unknown): value is OtherSlotSyncRunningDetails {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.runningKeyspace === "string" &&
    typeof candidate.startedAt === "number" &&
    Number.isFinite(candidate.startedAt) &&
    typeof candidate.triggeredBy === "string"
  );
}

type DemotionPreviewProps = {
  publications: Array<{ id: number; label: string }>;
  hasOverrides: boolean;
  policy: DemotionPolicy;
};

function DemotionPreview({ publications, hasOverrides, policy }: DemotionPreviewProps) {
  if (!hasOverrides || policy === "none") {
    return (
      <div className={styles.releaseDemotionPreviewEmpty} data-testid="release-demotion-preview">
        Publications dropped: None
      </div>
    );
  }
  if (publications.length === 0) {
    return (
      <div className={styles.releaseDemotionPreviewEmpty} data-testid="release-demotion-preview">
        Publications dropped: None
      </div>
    );
  }
  return (
    <div className={styles.releaseDemotionPreview} data-testid="release-demotion-preview">
      Publications dropped: {publications.map(pub => pub.label).join(", ")}
    </div>
  );
}

function DemotionPolicyHelp() {
  return (
    <div className={styles.demotionPolicyHelpContent}>
      <p>
        When promoting a publication, what should happen to the previous edition(s) it replaces?
      </p>
      <ul>
        <li>
          <strong>none</strong>: predecessors unchanged. Useful for preview slots where you want to
          see old and new editions side-by-side.
        </li>
        <li>
          <strong>one</strong>: the immediate predecessor flips to Archive. Matches production
          &ldquo;one Active edition per series&rdquo; rule. Default for go-live.
        </li>
      </ul>
    </div>
  );
}

function deriveKeyspaceError(value: string, touched: boolean): string | null {
  if (!touched) return null;
  if (!value) return "Slot name is required";
  if (!KEYSPACE_PATTERN.test(value)) {
    return "Only lowercase letters, digits, and dashes are allowed";
  }
  return null;
}

function buildReleaseDefinition({
  pubOverrides,
  demotionPolicy,
  asAt,
}: {
  pubOverrides: Map<number, PostStatus[]>;
  demotionPolicy: DemotionPolicy;
  asAt: string | undefined;
}): ReleaseDefinition | undefined {
  const hasOverrides = pubOverrides.size > 0;
  const hasAsAt = asAt !== undefined && asAt !== "";
  const isProductionDefault = !hasOverrides && demotionPolicy === "none" && !hasAsAt;
  if (isProductionDefault) return undefined;

  const publications: ReleasePublicationOverride[] = Array.from(pubOverrides.entries()).map(
    ([publicationId, postStatuses]) => ({
      publicationId,
      postStatuses,
    }),
  );

  const definition: ReleaseDefinition = {
    publications,
    demotionPolicy,
  };
  if (hasAsAt) definition.asAt = asAt;
  return definition;
}

function buildAsAtDateBounds(): { asAtMin: string; asAtMax: string } {
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const twoYearsAhead = new Date(today);
  twoYearsAhead.setFullYear(twoYearsAhead.getFullYear() + 2);
  const maxIso = twoYearsAhead.toISOString().slice(0, 10);
  return { asAtMin: todayIso, asAtMax: maxIso };
}

function computeDemotedPublications(
  pubOverrides: Map<number, PostStatus[]>,
  demotionPolicy: DemotionPolicy,
  allPublications: LegacyPublication[],
): Array<{ id: number; label: string }> {
  if (demotionPolicy !== "one" || pubOverrides.size === 0) return [];

  const byId = new Map(allPublications.map(pub => [pub.id, pub]));
  const visited = new Set<number>();
  const collected: number[] = [];

  for (const sourceId of pubOverrides.keys()) {
    const predecessorId = byId.get(sourceId)?.previousEditionId ?? null;
    if (predecessorId === null || visited.has(predecessorId)) continue;
    visited.add(predecessorId);
    collected.push(predecessorId);
  }

  return collected.map(id => {
    const pub = byId.get(id);
    return {
      id,
      label: pub ? `${pub.publication} (${pub.edition})` : `Publication ${id}`,
    };
  });
}

function buildCommandPreview({
  keyspace,
  clearAllData,
  allPageTypes,
  selectedPageTypes,
  seedFromKeyspace,
  releaseDefinition,
}: {
  keyspace: string;
  clearAllData: boolean;
  allPageTypes: string[] | null;
  selectedPageTypes: Set<string>;
  seedFromKeyspace: string | null;
  releaseDefinition: ReleaseDefinition | undefined;
}): string {
  const keyspaceToken = keyspace || "<keyspace>";
  const parts = [`sync --keyspace=${keyspaceToken}`];
  if (!clearAllData) parts.push("--no-flushdb");
  if (seedFromKeyspace !== null) parts.push(`--seed-from-keyspace=${seedFromKeyspace}`);
  if (releaseDefinition) {
    parts.push(`--release-definition=${JSON.stringify(releaseDefinition)}`);
  }
  const isSubset =
    allPageTypes !== null &&
    selectedPageTypes.size > 0 &&
    selectedPageTypes.size < allPageTypes.length;
  if (isSubset) {
    const sorted = Array.from(selectedPageTypes).sort();
    parts.push(`--pages=${sorted.join(",")}`);
  }
  return parts.join(" ");
}
